/**
 * Axios Configuration with JWT Token Handling & Auto-Refresh
 * 
 * This file sets up axios interceptors to:
 * 1. Automatically add JWT token to all requests
 * 2. Handle 401 (Unauthorized) errors with token refresh
 * 3. Retry failed requests after token refresh
 * 4. Redirect to login only when refresh fails
 */
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

// Request interceptor: Add JWT token to all requests
axiosInstance.interceptors.request.use(
(config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor: Handle 401 errors with token refresh
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {

        const originalRequest = error.config;
        const currentPath = window.location.pathname;

        // Skip refresh logic for login page and public routes
        if (currentPath === '/login' || currentPath.startsWith('/ai-agent/')) {
            return Promise.reject(error);
        }

        // Handle 401 Unauthorized (Token expired or invalid)
        if (error.response?.status === 401 && !originalRequest._retry) {
            const errorDetail = error.response?.data?.detail || '';

            // Check if this is a token expiration (not invalid credentials)
            const isTokenExpired = errorDetail.includes('expired') || errorDetail.includes('Token has expired');

            // Only attempt refresh for token expiration, not for other 401 errors
            if (isTokenExpired || !errorDetail) {
                // If already refreshing, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {

                    // Get refresh token from localStorage
                    const refreshToken = localStorage.getItem('refresh_token');

                    if (!refreshToken) {
                        throw new Error('No refresh token available');
                    }

                    // Attempt to refresh token using refresh token (not access token)
                    const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
                        refresh_token: refreshToken
                    });

                    const { access_token, refresh_token: new_refresh_token, user } = response.data;

                    // Update stored tokens and user
                    localStorage.setItem('token', access_token);
                    localStorage.setItem('refresh_token', new_refresh_token);
                    if (user) {
                        localStorage.setItem('user', JSON.stringify(user));
                    }

                    // Update authorization header for original request
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;

                    // Process queued requests with new token
                    processQueue(null, access_token);

                    // Retry original request with new token
                    return axiosInstance(originalRequest);

                } catch (refreshError) {
                    // Token refresh failed - logout user
                    processQueue(refreshError, null);

                    // Clear token, refresh token, and user data
                    localStorage.removeItem('token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');

                    // Dispatch custom event for App.js to handle
                    window.dispatchEvent(new CustomEvent('auth-expired'));

                    // Show error message
                    toast.error('Your session has expired. Please login again.');

                    // Redirect to login
                    setTimeout(() => {
                        window.location.replace('/login');
                    }, 100);

                    return Promise.reject(refreshError);

                } finally {
                    isRefreshing = false;
                }
            } else {
                // Not a token expiration - might be invalid credentials or other auth error
                // Don't attempt refresh, just logout
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
            
                window.dispatchEvent(new CustomEvent('auth-expired'));
                toast.error(errorDetail || 'Authentication failed. Please login again.');

                setTimeout(() => {
                    window.location.replace('/login');
                }, 100);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
